import { Theme as AugmentedTheme, useMediaQuery, useTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  MUIDataTable,
  SectionHeader,
  Typography,
  useDataTable,
  useSnackbarHandler,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import {
  useDeleteExamRegistrationDraft,
  useGetRegisteredSubject,
  usePutDrafSubjectWithdrawal,
  usePutUndoDrafSubjectWithdrawal,
} from '../../../api/candidate/exam/@candidate/queries-services/queries';
import { ViewSelectedSubjectPapers } from '../../../api/candidate/exam/model';
import DeleteButton from '../../../components/buttons/DeleteButton';
import EditButton from '../../../components/buttons/EditButton';
import EditExamAmendmentModal from '../../../components/EditExamAmendmentModal';
import FormHeader from '../../../components/form-items/FormHeader';
import { onEditModalOpenHandler } from '../../../utils/ApiUtils';
import { getKeyByValue } from '../../../utils/ArrayUtils';
import WithdrawalModal from './SubjectAmendment/WithdrawalModal';

import RouteKey from '../../../routes/key';
import { setSubjectDetails } from '../../../utils/StringUtils';
import { candidateService } from '../../../api';

const useStyles = makeStyles()((theme) =>
({
  textContainer: {
    marginBottom: theme.spacing(3),
  },
  subjectContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  subjectText: {
    width: '100%',
  },
  actionButton: {
    padding: 0,

    '& span': {
      justifyContent: 'flex-start',
    },
  },
  editContainer: {
    marginBottom: theme.spacing(6),
  },
  deleteButton: {
    color: '#E55934',
  },
  deleteContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(6),
  },
  button: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  },
  buttonContainer: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  optionalPapers: {
    color: theme.color.text.textTertiary,
  },
}));

const {
  examRegistrations,
  addSubject,
  reviewSubjects,
  coordinatorExamRegistrations,
  coordinatorAddSubject,
  coordinatorReviewSubjects,
} = RouteKey.amendment.child;
const {
  examRegistrations: candidateWithdrawal,
  withdrawal: coordinatorWithdrawal,
  instruction,
} = RouteKey.withdrawal.child;
const { amendment, withdrawal } = RouteKey.child;

/** define Mui DataTable Column Definition
 * */
const TableColumns = (
  translations: any,
  registeredData: any,
  openEditModalStateHandler: any,
  deleteDraft: any,
  applicationUUID: any,
  setSelected: any,
  setWithdrawalModal: any
) => {
  const { classes } = useStyles();
  const checkEditColumn = () =>
    registeredData && registeredData.registeredSubjects.find((subject: any) => subject.editable);
  const checkToRenderEditColumn = !!checkEditColumn();
  return [
    {
      name: 'subjectName',
      label: translations.subjectName,
      options: {
        sort: false,
        searchable: true,
        customBodyRenderLite: (dataIndex: number, rowIndex: number) => {
          const optionalPaperList: string[] = [];
          if (registeredData) {
            registeredData.registeredSubjects[
              dataIndex
            ].optionalPapers.forEach((item: ViewSelectedSubjectPapers) =>
              optionalPaperList.push(`Paper ${item.paperNo}`)
            );
          }

          return (
            <Box className={classes.subjectContainer}>
              {registeredData && (
                <Typography className={classes.subjectText}>
                  {registeredData.registeredSubjects[dataIndex].subjectName} (
                  {registeredData.registeredSubjects[dataIndex].subjectCode})
                </Typography>
              )}

              {optionalPaperList.length ? (
                <Typography className={classes.optionalPapers}>
                  + Optional Papers: {optionalPaperList.join(', ')}
                </Typography>
              ) : null}
            </Box>
          );
        },
      },
    },

    {
      name: 'subjectLevel',
      label: translations.subjectLevel,
      options: {
        sort: false,
        searchable: true,
      },
    },
    {
      name: 'examSeries',
      label: translations.examSeries,
      options: {
        sort: false,
        searchable: true,
        setCellProps: () => ({ style: { minWidth: '130px', maxWidth: '130px' } }),
      },
    },
    {
      name: 'examLevel',
      label: translations.examLevel,
      options: {
        sort: false,
        searchable: true,
        setCellProps: () => ({ style: { minWidth: '180px', maxWidth: '180px' } }),
      },
    },
    {
      name: 'language',
      label: translations.languageMedium,
      options: {
        sort: false,
        searchable: true,
      },
    },
    {
      name: 'action',
      label: translations.action,
      options: {
        sort: false,
        searchable: false,
        display: checkToRenderEditColumn,
        setCellProps: () => ({ style: { minWidth: '110px', maxWidth: '110px' } }),

        customBodyRenderLite: (dataIndex: any) => (
          <Box>
            {registeredData &&
              !registeredData.registeredSubjects[dataIndex].nonActionable &&
              registeredData.registeredSubjects[dataIndex].examSeries.length > 1 &&
              registeredData.registeredSubjects[dataIndex].editable && (
                <>
                  <EditButton
                    label="Edit"
                    buttonId={dataIndex}
                    editHandler={() =>
                      onEditModalOpenHandler(
                        registeredData.registeredSubjects[dataIndex].examRegistrationDetailUuid,
                        openEditModalStateHandler,
                        registeredData.registeredSubjects[dataIndex].subjectCode
                      )
                    }
                  />
                </>
              )}
          </Box>
        ),
      },
    },
    {
      name: '',
      label: `${checkToRenderEditColumn ? '' : translations.action}`,
      options: {
        sort: false,
        searchable: false,

        customBodyRenderLite: (dataIndex: any) => (
          <>
            {registeredData &&
              !registeredData.registeredSubjects[dataIndex].nonActionable &&
              registeredData.registeredSubjects.length > 1 && (
                <DeleteButton
                  buttonId={dataIndex}
                  label="Delete"
                  deleteHandler={() => {
                    deleteDraft({
                      uuid: applicationUUID,
                      subjectCode: registeredData.registeredSubjects[dataIndex].subjectCode,
                      callbackVariable: {
                        subjectName: registeredData.registeredSubjects[dataIndex].subjectName,
                        subjectLevel: registeredData.registeredSubjects[dataIndex].subjectLevel,
                        subjectCode: registeredData.registeredSubjects[dataIndex].subjectCode,
                      },
                    });
                  }}
                />
              )}

            {registeredData &&
              !registeredData.registeredSubjects[dataIndex].nonActionable &&
              registeredData.registeredSubjects.length === 1 && (
                <DeleteButton
                  buttonId={dataIndex}
                  label="Delete"
                  deleteHandler={() => {
                    setSelected({
                      subjectCode: registeredData.registeredSubjects[dataIndex].subjectCode,
                      subjectLevel: registeredData.registeredSubjects[dataIndex].subjectLevel,
                      subjectName: registeredData.registeredSubjects[dataIndex].subjectName,
                    });
                    setWithdrawalModal(true);
                  }}
                />
              )}
          </>
        ),
      },
    },
  ];
};

/** define Mui DataTable Delete Column Definition
 * */
const DeleteTableColumns = (
  translations: any,
  registeredData: any,
  t: any,
  undoDelete: any,
  applicationUUID: any
) => {
  const { classes } = useStyles();

  return [
    {
      name: 'subjectName',
      label: translations.subjectName,
      options: {
        sort: false,
        searchable: true,
        setCellProps: () => ({ style: { minWidth: '310px', maxWidth: '310px' } }),
        customBodyRenderLite: (dataIndex: number, rowIndex: number) => {
          const optionalPaperList: string[] = [];
          if (registeredData) {
            registeredData.deletedSubjects[
              dataIndex
            ].optionalPapers.forEach((item: ViewSelectedSubjectPapers) =>
              optionalPaperList.push(`${t('exam->>ViewSelectedSubjects.Paper')} ${item.paperNo}`)
            );
          }

          return (
            <Box className={classes.subjectContainer}>
              {registeredData && (
                <Typography className={`${classes.subjectText}`} id={`subjectName-${dataIndex}`}>
                  {registeredData.deletedSubjects[dataIndex].subjectName} (
                  {registeredData.deletedSubjects[dataIndex].subjectCode})
                </Typography>
              )}

              {optionalPaperList.length ? (
                <Typography className={`${classes.optionalPapers}   subject-optional-delete`}>
                  {t('exam->>ViewSelectedSubjects.OptionalPapers')}: {optionalPaperList.join(', ')}
                </Typography>
              ) : null}
            </Box>
          );
        },
      },
    },

    {
      name: 'subjectLevel',
      label: translations.subjectLevel,
      options: {
        sort: false,
        searchable: true,
      },
    },
    {
      name: 'examSeries',
      label: translations.examSeries,
      options: {
        sort: false,
        searchable: true,
        setCellProps: () => ({ style: { minWidth: '130px', maxWidth: '130px' } }),
      },
    },
    {
      name: 'examLevel',
      label: translations.examLevel,
      options: {
        sort: false,
        searchable: true,
        setCellProps: () => ({ style: { minWidth: '180px', maxWidth: '180px' } }),
      },
    },
    {
      name: 'language',
      label: translations.languageMedium,
      options: {
        sort: false,
        searchable: true,
      },
    },
    {
      name: 'action',
      label: translations.action,
      options: {
        sort: false,
        searchable: false,
        setCellProps: () => ({ style: { minWidth: '240px', maxWidth: '240px' } }),

        customBodyRenderLite: (dataIndex: any) => (
          <Box>
            {registeredData && (
              <Button
                className={classes.actionButton}
                variant="text"
                id={`button-undo-delete-${dataIndex}`}
                color="primary"
                onClick={() => {
                  undoDelete({
                    uuid: applicationUUID,
                    subjectCode: registeredData.deletedSubjects[dataIndex].subjectCode,
                    callbackVariable: {
                      subjectName: registeredData.deletedSubjects[dataIndex].subjectName,
                      subjectLevel: registeredData.deletedSubjects[dataIndex].subjectLevel,
                      subjectCode: registeredData.deletedSubjects[dataIndex].subjectCode,
                    },
                  });
                }}
                size="small"
              >
                <IconWithLabel
                  iconSize="M"
                  iconName={IconName.undo}
                  label="Undo delete"
                  typographyProps={{
                    variant: 'h5',
                  }}
                />
              </Button>
            )}
          </Box>
        ),
      },
    },
  ];
};

const EditSubject = ({
  translations,
  next,
  skip,
  isGroup,
}: {
  translations: {
    subjectName: string;
    subjectCode: string;
    subjectLevel: string;
    examSeries: string;
    examLevel: string;
    languageMedium: string;
    action: string;
  };
  isGroup: boolean;
  next?: () => void;
  skip?: () => void;
}) => {
  const { classes } = useStyles();

  const { t } = useTranslation();

  const { uuid: applicationUUID } = useParams<any>();
  const history = useHistory();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { state, dispatch: tableDispatch, tOptions } = useDataTable();
  const { snackbarHandler } = useSnackbarHandler();

  const [selected, setSelected] = useState<any>();
  const [openEditModal, setOpenEditModal] = useState<Record<string, boolean>>({});
  const openEditModalStateHandler = { openEditModal, setOpenEditModal };
  const [withdrawalModal, setWithdrawalModal] = useState(false);

  const { data: registeredData, refetch: refetchData } = useGetRegisteredSubject(applicationUUID);
  const {
    mutate: deleteDraft,
    status: deleteDraftStatus,
    reset: resetDeleteDraftStatus,
  } = usePutDrafSubjectWithdrawal(
    (res, variable) => {
      refetchData();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('exam->>SubjectSnackbar.HasBeenRemoved', {
          subjectDetails: setSubjectDetails(variable.callbackVariable),
        }),
      });
    },
    (res, variable) => {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'error',
        targetData: t('exam->>SubjectSnackbar.UnableToRemove', {
          subjectDetails: setSubjectDetails(variable.callbackVariable),
        }),
        targetAnchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });
    }
  );

  const { mutate: undoDelete } = usePutUndoDrafSubjectWithdrawal(
    (res, variable) => {
      refetchData();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('exam->>SubjectSnackbar.HasBeenRestored', {
          subjectDetails: setSubjectDetails(variable.callbackVariable),
        }),
      });
    },
    () => {
      // Do Nothing
    }
  );

  const { data: examTypeData } = candidateService.exam.candidate.useGetExamRegistration(
    applicationUUID,
    {
      enabled: isGroup,
    }
  );

  const deleteAmendmentDraft = useDeleteExamRegistrationDraft(
    () => {
      if (isGroup) {
        history.push(
          `/${withdrawal}/${coordinatorWithdrawal.route}/${instruction.route}/${examTypeData?.examType.code}`
        );
      } else {
        history.push(`/${withdrawal}/${candidateWithdrawal.route}/${instruction.route}`);
      }
    },
    () => {
      // Do Nothing
    },
    () => {
      // Do Nothing
    }
  );

  const onWithdrawalCloseHandler = () => {
    setWithdrawalModal(false);
  };

  const addSubjectUrl: string = isGroup
    ? `/${amendment}/${coordinatorExamRegistrations.route}/${coordinatorAddSubject.route}/${applicationUUID}`
    : `/${amendment}/${examRegistrations.route}/${applicationUUID}/${addSubject.route}`;

  const skipAddSubjectUrl: string = isGroup
    ? `/${amendment}/${coordinatorExamRegistrations.route}/${coordinatorReviewSubjects.route}/${applicationUUID}`
    : `/${amendment}/${examRegistrations.route}/${applicationUUID}/${reviewSubjects.route}`;

  useEffect(() => {
    if (withdrawalModal) {
      resetDeleteDraftStatus();
    }
  }, [resetDeleteDraftStatus, withdrawalModal]);

  return (
    <>
      <EditExamAmendmentModal
        applicationUuid={applicationUUID}
        subjectCode={getKeyByValue(openEditModal, true)}
        editModalHandler={openEditModalStateHandler}
        examRegistrationDetailUuid={getKeyByValue(openEditModal, false)}
      />

      <WithdrawalModal
        onClickWithdraw={() => {
          deleteDraft(
            {
              uuid: applicationUUID,
              subjectCode: selected.subjectCode,
              callbackVariable: {
                subjectName: selected.subjectName,
                subjectLevel: selected.subjectLevel,
                subjectCode: selected.subjectCode,
              },
            },
            {
              onSuccess: () => {
                deleteAmendmentDraft.mutate(applicationUUID);
              },
            }
          );
        }}
        onClose={onWithdrawalCloseHandler}
        modelOpen={withdrawalModal}
        status={deleteDraftStatus}
      />

      <SectionHeader title="Edit Registered Subject(s)" />

      <Typography className={classes.textContainer}>
        Click ‘Edit’ to drop or make changes to the subjects you have registered for. Click ‘Undo
        delete’ to undo subject deletion.
      </Typography>

      <Box className={classes.editContainer}>
        <MUIDataTable
          // @ts-ignore
          state={state}
          dispatch={tableDispatch}
          columns={TableColumns(
            translations,
            registeredData,
            openEditModalStateHandler,
            deleteDraft,
            applicationUUID,
            setSelected,
            setWithdrawalModal
          )}
          // @ts-ignore
          data={registeredData?.registeredSubjects}
          options={{
            ...tOptions,
          }}
        />
      </Box>

      <FormHeader title={t('amendment->>EditSubject.Deleted Subject(s)')} />

      <Box className={classes.deleteContainer}>
        <MUIDataTable
          // @ts-ignore
          state={state}
          dispatch={tableDispatch}
          columns={DeleteTableColumns(translations, registeredData, t, undoDelete, applicationUUID)}
          // @ts-ignore
          data={registeredData?.deletedSubjects}
          options={{
            ...tOptions,
          }}
        />
      </Box>

      <Box className={classes.buttonContainer}>
        <Button
          id="button-addSubject"
          className={classes.button}
          type="button"
          color="secondary"
          fullWidth={isMobile}
          onClick={() => {
            history.push(addSubjectUrl);
          }}
        >
          Add Subject
        </Button>

        <Button
          className={classes.button}
          type="button"
          color="secondary"
          variant="outlined"
          id="button-skip-add-subject"
          fullWidth={isMobile}
          onClick={() => {
            history.push(skipAddSubjectUrl);
          }}
        >
          Skip Add Subject
        </Button>
      </Box>
    </>
  );
};

export default EditSubject;
